/**
 * PlaybookEditorPage — Brief 4 (Gestor) · Tela 1 Editor de Playbooks (PR1 · Foundation).
 *
 * Rota: /admin/ia/playbooks. Modo leitura sobre o CRUD real de Playbooks.
 * Padrão D-B (defense-in-depth): gate próprio na página (`playbook-read`), além
 * da query gateada no hook — não confia só na rota/nav.
 *
 * Escopo PR1: lista de playbooks → editor shell (9 abas) → aba Geral em leitura.
 * Edição (gate `playbook-author`), demais abas e simulador chegam em fatias futuras.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { PlaybookEditorView } from '../features/playbooks/PlaybookEditorView';
import { PlaybookListView } from '../features/playbooks/PlaybookListView';
import { usePlaybook, usePlaybookList } from '../features/playbooks/use-playbooks';
import { useCapability } from '../hooks/use-capability';

import '../features/playbooks/playbooks-4.css';

export function PlaybookEditorPage(): JSX.Element {
  const canRead = useCapability('playbook-read');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canRead) {
    return (
      <div className="playbooks-screen playbooks-screen--gate">
        <CapabilityLockedBanner
          capability="playbook-read"
          tierMin="professional"
          fallbackMessage="O editor de playbooks é restrito a perfis de gestão — fale com o administrador do tenant."
        />
      </div>
    );
  }

  return <PlaybookEditorPageInner />;
}

function PlaybookEditorPageInner(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const list = usePlaybookList();
  const detail = usePlaybook(selectedId);

  if (selectedId !== null) {
    if (detail.isLoading) {
      return (
        <div className="playbooks-screen">
          <div className="pb-panel">
            <p className="pb-panel__subtitle">Carregando playbook…</p>
          </div>
        </div>
      );
    }
    if (detail.isError || detail.data === undefined) {
      return (
        <div className="playbooks-screen">
          <div className="pb-panel">
            <p className="pb-alert" role="alert">
              Não foi possível carregar este playbook.
            </p>
            <button type="button" className="pb-btn" onClick={() => setSelectedId(null)}>
              Voltar à lista
            </button>
          </div>
        </div>
      );
    }
    return <PlaybookEditorView playbook={detail.data} onBack={() => setSelectedId(null)} />;
  }

  if (list.isLoading) {
    return (
      <div className="playbooks-screen">
        <div className="pb-panel">
          <p className="pb-panel__subtitle">Carregando playbooks…</p>
        </div>
      </div>
    );
  }
  if (list.isError) {
    return (
      <div className="playbooks-screen">
        <div className="pb-panel">
          <p className="pb-alert" role="alert">
            Falha ao carregar os playbooks.
          </p>
        </div>
      </div>
    );
  }

  return <PlaybookListView playbooks={list.data} onOpen={(id) => setSelectedId(id)} />;
}
